import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Providers } from "@chotca/app-shell";
import { NextIntlClientProvider } from "next-intl";
import { LOCALE, MESSAGES } from "@/boot/i18n";
import { AuthGate } from "@/components/AuthGate";
import { AnalyticsIdentitySync } from "@/components/analytics-identity-sync";
import { EmployerAppShell } from "@/components/EmployerAppShell";
import { DashboardClient } from "@/routes/dashboard-client";
import { PostingsListClient } from "@/routes/postings/list-client";
import { ApplicantsClient } from "@/routes/applicants/applicants-client";
import { IdentityRequiredGate } from "@/components/employer/identity-required-gate";
import { CandidatesClient } from "@/routes/candidates/candidates-client";
import { InterviewsClient } from "@/routes/interviews/interviews-client";
import { NewPostingForm } from "@/routes/postings/new/new-posting-form";
import { PostingDetailClient } from "@/routes/postings/[id]/posting-detail-client";
import { EditPostingForm } from "@/routes/postings/[id]/edit/edit-posting-form";
import { TemplatesClient } from "@/routes/postings/templates/templates-client";
import { OrgSettingsClient } from "@/routes/org/org-settings-client";
import { BulkBranchesClient } from "@/routes/org/branches/bulk-branches-client";
import { BillingClient } from "@/routes/billing/billing-client";
import { InvoiceDetailClient } from "@/routes/billing/invoice-detail-client";
import { MembersClient } from "@/routes/org/members/members-client";
import { KycSubmitClient } from "@/routes/org/kyc/kyc-submit-client";
import { OrgActivityClient } from "@/routes/org/activity/org-activity-client";
import { OnboardingEmployerPage } from "@/routes/onboarding-pages/employer/page";
import { ProfileClient } from "@/routes/profile/profile-client";
import { IdentitySubmitClient } from "@/routes/profile/identity/identity-submit-client";
import { LoginPage } from "@/routes/auth/login-page";
import { RegisterPage } from "@/routes/auth/register-page";
import { ForgotPasswordPage } from "@/routes/auth/forgot-password-page";
import { ResetPasswordPage } from "@/routes/auth/reset-password-page";
import { VerifyEmailPage } from "@/routes/auth/verify-email-page";
import { ClaimPage } from "@/routes/auth/claim-page";
import { WelcomePage } from "@/routes/welcome-page";

function RootShell() {
  return (
    <NextIntlClientProvider locale={LOCALE} messages={MESSAGES}>
      <Providers>
        <Outlet />
      </Providers>
    </NextIntlClientProvider>
  );
}

function EmployerArea() {
  return (
    <AuthGate role="EMPLOYER">
      {(me) => (
        <EmployerAppShell>
          <AnalyticsIdentitySync me={me} />
          <Outlet />
        </EmployerAppShell>
      )}
    </AuthGate>
  );
}

function PostingDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <PostingDetailClient id={id!} />;
}

function EditPostingRoute() {
  const { id } = useParams<{ id: string }>();
  return <EditPostingForm id={id!} />;
}

export const router = createBrowserRouter([
  {
    element: <RootShell />,
    children: [
      { path: "/", element: <Navigate to="/dashboard" replace /> },
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      { path: "/verify-email", element: <VerifyEmailPage /> },
      { path: "/claim/:token", element: <ClaimPage /> },
      { path: "/welcome", element: <WelcomePage /> },
      { path: "/onboarding/employer", element: <OnboardingEmployerPage /> },
      {
        element: <EmployerArea />,
        children: [
          { path: "/dashboard", element: <DashboardClient /> },
          {
            path: "/applicants",
            element: (
              <IdentityRequiredGate>
                <ApplicantsClient />
              </IdentityRequiredGate>
            ),
          },
          {
            path: "/candidates",
            element: (
              <IdentityRequiredGate>
                <CandidatesClient />
              </IdentityRequiredGate>
            ),
          },
          { path: "/interviews", element: <InterviewsClient /> },
          { path: "/postings", element: <PostingsListClient /> },
          { path: "/postings/new", element: <NewPostingForm /> },
          { path: "/postings/templates", element: <TemplatesClient /> },
          { path: "/postings/:id", element: <PostingDetailRoute /> },
          { path: "/postings/:id/edit", element: <EditPostingRoute /> },
          { path: "/billing", element: <BillingClient /> },
          {
            path: "/billing/:orderCode",
            element: <InvoiceDetailClient />,
          },
          { path: "/org", element: <OrgSettingsClient /> },
          { path: "/org/branches/bulk", element: <BulkBranchesClient /> },
          { path: "/org/members", element: <MembersClient /> },
          { path: "/org/kyc", element: <KycSubmitClient /> },
          { path: "/org/activity", element: <OrgActivityClient /> },
          { path: "/profile", element: <ProfileClient /> },
          { path: "/profile/identity", element: <IdentitySubmitClient /> },
        ],
      },
    ],
  },
]);
